/** @file Uniform-grid spatial hash over curve-graph points: O(1) pick of the key, handle or curve. */
import { CURVE_POINT } from './curvePoint.data';
import type { CurvePoints } from './curveTypes';

/** Points bucketed by square cells (CSR layout): cell c holds items[start[c] .. start[c + 1]). */
export interface CurveHitGrid {
  cell: number;
  cols: number;
  rows: number;
  start: Int32Array;
  items: Int32Array;
}

/** A pick candidate; `tier` is 1 for keys and handles, 0 for curve samples. */
type Candidate = { index: number; tier: number; d2: number; kind: number };

function cellIndex(grid: Pick<CurveHitGrid, 'cell' | 'cols' | 'rows'>, x: number, y: number): number {
  const cx = Math.floor(x / grid.cell);
  const cy = Math.floor(y / grid.cell);
  return cx >= 0 && cx < grid.cols && cy >= 0 && cy < grid.rows ? cy * grid.cols + cx : -1;
}

/** Keys and handles beat samples; then nearer wins; then handle over key; then the later point. */
function beats(a: Candidate, b: Candidate): boolean {
  if (a.tier !== b.tier) return a.tier > b.tier;
  if (a.d2 !== b.d2) return a.d2 < b.d2;
  if (a.kind !== b.kind) return a.kind > b.kind;
  return a.index > b.index;
}

function consider(points: CurvePoints, i: number, x: number, y: number, tol2: number, best: Candidate): void {
  const dx = (points.x[i] ?? Number.NaN) - x;
  const dy = (points.y[i] ?? Number.NaN) - y;
  const d2 = dx * dx + dy * dy;
  if (!(d2 <= tol2)) return;
  const kind = points.kind[i] ?? CURVE_POINT.sample;
  const candidate = { index: i, tier: kind === CURVE_POINT.sample ? 0 : 1, d2, kind };
  if (beats(candidate, best)) Object.assign(best, candidate);
}

/** Bucketing and picking; cost of a pick is the points in the cells within the tolerance, not the point count. */
export const curveHitGrid = {
  /** Buckets the points inside a width x height canvas into cells of `cell` pixels (points outside are dropped). */
  build(points: CurvePoints, width: number, height: number, cell: number): CurveHitGrid {
    const shape = { cell, cols: Math.max(1, Math.ceil(width / cell)), rows: Math.max(1, Math.ceil(height / cell)) };
    const cells = points.x.map((x, i) => cellIndex(shape, x, points.y[i] ?? Number.NaN));
    const start = new Int32Array(shape.cols * shape.rows + 1);
    for (const c of cells) if (c >= 0) start[c + 1] = (start[c + 1] ?? 0) + 1;
    for (let c = 1; c < start.length; c++) start[c] = (start[c] ?? 0) + (start[c - 1] ?? 0);
    const next = start.slice(0, -1);
    const items = new Int32Array(start[start.length - 1] ?? 0);
    cells.forEach((c, i) => {
      if (c < 0) return;
      const at = next[c] ?? 0;
      items[at] = i;
      next[c] = at + 1;
    });
    return { ...shape, start, items };
  },
  /** Index of the best point within `tolerance` pixels of (x, y), or -1. */
  pick(grid: CurveHitGrid, points: CurvePoints, x: number, y: number, tolerance: number): number {
    const best: Candidate = { index: -1, tier: -1, d2: Infinity, kind: -1 };
    const reach = Math.ceil(tolerance / grid.cell);
    const cx = Math.floor(x / grid.cell);
    const cy = Math.floor(y / grid.cell);
    for (let j = Math.max(0, cy - reach); j <= Math.min(grid.rows - 1, cy + reach); j++) {
      for (let i = Math.max(0, cx - reach); i <= Math.min(grid.cols - 1, cx + reach); i++) {
        const c = j * grid.cols + i;
        const end = grid.start[c + 1] ?? 0;
        for (let k = grid.start[c] ?? 0; k < end; k++) consider(points, grid.items[k] ?? -1, x, y, tolerance * tolerance, best);
      }
    }
    return best.index;
  },
};
